import React from 'react';
import { injectStripe } from 'react-stripe-elements';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import {
  FDAlert,
  FDBox,
  FDPaper,
  FDStack,
} from '@facet-wealth/design';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useScrollTopOnMount from 'Hooks/useScrollTopOnMount';
import useEventTrackingLogEvent, { categories, eventSubTypes, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import StepNavigationFooter from 'Packages/FacetDesign/Components/Experimental/StepNavigationFooter';
import { dataAttrs } from 'Helpers/Testing';
import {
  useBuildCommonFQEventProperties,
} from 'Views/FreemiumEntry/HasRelationship/Content/Components/utils';
import ErrorList from 'Components/UI/ErrorList';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import Divider from 'Components/UI/Divider';
import {
  ValuePropositions,
} from 'Views/FreemiumEntry/HasRelationship/Content/Templates/PaymentPage/ValuePropositions';
import segmentEvents from 'EventTracking/consts/segmentEvents';
import { useSubmitHandler } from './useSubmit';


const PaymentPageContent = ({ stripe }) => {
  const {
    resource, consumeAttr,
  } = useMetaLayoutContext();
  const { logStandardizedEvent, logEvent } = useEventTrackingLogEvent({ nodeName: 'eqfPaymentPage' });
  const { buildCommonFQEventProperties } = useBuildCommonFQEventProperties();

  useScrollTopOnMount();

  useBoneBackgroundColor();

  const {
    onSubmit,
    metaErrors,
    stripeError,
    loading,
    disabled,
  } = useSubmitHandler({
    stripe,
    onSubmitEvent: () => logEvent(segmentEvents.leadSubmittedPaymentStep, { email: resource.attr('email') }),
    onErrorEvent: () => logEvent(segmentEvents.leadPaymentError, { email: resource.attr('email') }),
  });

  let error = null;
  if (stripeError) {
    error = (
      <FDAlert
        title="Error processing credit card"
        description={stripeError}
        status="error"
        {...dataAttrs('stripe error')}
      />
    );
  } else if (metaErrors.length) {
    error = <ErrorList errors={metaErrors} title="Error" hideErrorTitle />;
  }

  return (
    <FDStack direction={'column'} spacing={4} {...dataAttrs('payment-page-container')}>
      <FDStack direction={'column'} spacing={4}>
        <FDStack direction={'column'} spacing={3}>
          <MetaControlField attr={'insightProductTitle'} />
          <FDPaper padding={3} appearance="elevated">
            <FDStack direction="column" spacing={1}>
              <FDStack direction="row" spacing={1} justifyContent="space-between" wrap>
                <MetaControlField attr={'dueTodayText'} />
                <MetaControlField attr={'dueToday'} />
              </FDStack>
              <Divider color="attention" />
              <ValuePropositions bulletPoints={consumeAttr('valuePropositions')} />
            </FDStack>
          </FDPaper>
          <FDPaper padding={3} appearance="elevated" flex={1} minWidth={300}>
            <FDBox flexGrow={1}>
              <FDStack direction="column" spacing={2}>
                <MetaControlField attr="stripeToken" />
              </FDStack>
            </FDBox>
          </FDPaper>
          <MetaControlField attr={'agreementDetails'} />
          {error /* Putting errors here ensures they are visible in both desktop and mobile, regardless of scrolling position */}
          <MetaControlField attr={'agreementCheckbox'} {...dataAttrs('terms and conditions')} />
        </FDStack>
        {resource.meta().canPut() && <StepNavigationFooter
          onNext={(selection) => {
            logStandardizedEvent(categories.facetQuiz, eventTypes.processCreditCard, eventSubTypes.clickedCTA, {
              eventProperties: {
                ...buildCommonFQEventProperties(resource),
              },
            });
            onSubmit(selection);
          }}
          disabled={disabled}
          isLoading={loading}
          label={resource.attr('buttonText') || 'Continue'}
        />}
      </FDStack>
    </FDStack>
  );
};

export default injectStripe(PaymentPageContent);
